import styled from "@emotion/styled";
import type { Wallet } from "@solana/wallet-adapter-react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { CurrentViewport } from "@stepfinance/react";
import { darkGrey8, StepTextInput, useViewportWidth } from "@stepfinance/react";
import React, { useEffect } from "react";
import type { URLSearchParamsInit } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useAddressInputStore } from "../../hooks/stores/addressInputStore";
import { getAccountFromDomainRegistry } from "../../utils/utils";
import { isPublicKeyWalletAdapter } from "../../utils/wallet/providers";
import { SearchIcon } from "../Icons";

export const watchWallet = async (
  address: string,
  connection: Connection,
  wallet: Wallet | null,
  searchParams: URLSearchParams,
  setSearchParams: (
    nextInit: URLSearchParamsInit,
    navigateOptions?:
      | {
          replace?: boolean | undefined;
          state?: any;
        }
      | undefined,
  ) => void,
): Promise<void> => {
  try {
    const input = address.trim();
    let viewAddress: PublicKey;
    if (input.toLowerCase().endsWith(".sol")) {
      viewAddress = await getAccountFromDomainRegistry(input, connection);
    } else {
      viewAddress = new PublicKey(input);
    }
    if (viewAddress) {
      setSearchParams({ watching: viewAddress.toBase58() });
      // Switching from watched to watched doesn't auto-disconnect, so we trigger it here.
      if (isPublicKeyWalletAdapter(wallet) && input !== searchParams.get("watching")) {
        void wallet?.adapter.disconnect();
      }
    }
  } catch (err) {
    console.error("AddressInput", err);
  }
};

export const AddressInput: React.FC = () => {
  const { address, setAddress } = useAddressInputStore();
  const { connection } = useConnection();
  const { publicKey, watching, wallet } = useEnrichedWallet();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentViewport } = useViewportWidth();

  const {
    currentLanguageData: {
      pages: { allPages },
    },
  } = useLanguageContext();

  useEffect(() => {
    if (watching && publicKey) {
      setAddress(publicKey.toBase58());
    } else {
      setAddress("");
    }
  }, [publicKey, setAddress, watching]);

  return (
    <AddressInputContainer currentViewport={currentViewport}>
      <StepTextInput
        placeholder={allPages.topBar.enterWalletPlaceholder}
        onChange={(event) => setAddress(event.currentTarget.value)}
        value={address}
        variant="filled"
        borderRadius="large"
        onPressEnter={() => watchWallet(address, connection, wallet, searchParams, setSearchParams)}
        SuffixIcon={<SearchIcon width={14} height={16} color={darkGrey8} />}
      />
    </AddressInputContainer>
  );
};

const AddressInputContainer = styled.div<{ currentViewport?: CurrentViewport }>`
  width: ${(props) => (props.currentViewport === "Mobile" ? "100%" : "450px")};
`;
